/**
 * Edição em tela cheia — o segundo passo do padrão, e a **gestão completa** de uma
 * entidade: todos os campos da tabela (agrupados) mais todas as correlações. Busca a
 * entidade por id no mount (não confia no objeto da lista, que é resumido). Salvar é
 * um PATCH com só o que mudou; excluir é soft-delete e volta para a lista.
 *
 * `telaEdicaoCustom` no descriptor troca esta tela inteira (rastreador). Sem PATCH
 * (`podeEditar=false`), os campos ficam desabilitados — visíveis, não editáveis.
 */
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BarraAcoesEdicao } from '@/components/crud/BarraAcoesEdicao';
import { GrupoCampos } from '@/components/crud/GrupoCampos';
import { PaineisEdicao } from '@/components/crud/PaineisEdicao';
import type { ValorCampo } from '@/components/crud/CampoDinamico';
import { Abas } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Pagina } from '@/components/ui/pagina';
import { Marca } from '@/components/Logo';
import { useAbaNaUrl } from '@/lib/abaNaUrl';
import { atualizarRecurso, obterRecursoPorId, removerRecurso } from '@/lib/cadastroApi';
import {
  corpoEdicao,
  todosOsCampos,
  valoresIniciais,
  type Valores,
} from '@/lib/recursos/formulario';
import type { AbaEdicao, DescritorRecurso, RecursoBase } from '@/lib/recursos/tipos';
import { cn } from '@/lib/utils';

export function EdicaoRecurso({ descritor, id }: { descritor: DescritorRecurso; id: string }) {
  const navegar = useNavigate();
  const [item, setItem] = useState<RecursoBase | null>(null);
  const [naoEncontrado, setNaoEncontrado] = useState(false);
  const [valores, setValores] = useState<Valores>({});
  const [original, setOriginal] = useState<Valores>({});
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  // A aba aberta vive na URL, não aqui: F5 volta na mesma aba, o link colado num chat abre onde
  // a conversa está, e «voltar» desfaz a troca de aba. Ver `lib/abaNaUrl`.
  const [abaAtiva, setAbaAtiva] = useAbaNaUrl(descritor.abas);

  const campos = useMemo(() => todosOsCampos(descritor.gruposEdicao), [descritor]);

  const carregar = useCallback(async () => {
    const r = await obterRecursoPorId<RecursoBase>(descritor.endpoint, id);
    if (!r.ok) {
      if (r.status === 404) setNaoEncontrado(true);
      else toast.error(r.erro);
      return;
    }
    setItem(r.dados);
    const iniciais = valoresIniciais(campos, r.dados);
    setValores(iniciais);
    setOriginal(iniciais);
  }, [descritor.endpoint, id, campos]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const temMudancas = useMemo(
    () => campos.some((c) => valores[c.chave] !== original[c.chave]),
    [campos, valores, original],
  );

  function ajustar(chave: string, v: ValorCampo) {
    setValores((atual) => ({ ...atual, [chave]: v }));
  }

  async function salvar() {
    const corpo = corpoEdicao(descritor.gruposEdicao, valores, original);
    if (Object.keys(corpo).length === 0) return;
    setSalvando(true);
    const r = await atualizarRecurso<RecursoBase>(descritor.endpoint, id, corpo);
    setSalvando(false);
    if (!r.ok) return void toast.error(r.erro);
    setItem(r.dados);
    const iniciais = valoresIniciais(campos, r.dados);
    setValores(iniciais);
    setOriginal(iniciais);
    toast.success('Alterações salvas.');
  }

  async function excluir() {
    setExcluindo(true);
    const r = await removerRecurso(descritor.endpoint, id);
    setExcluindo(false);
    if (!r.ok) return void toast.error(r.erro);
    toast.success(`${descritor.rotuloItem(item as RecursoBase)} arquivado.`);
    navegar(`/cadastro/${descritor.chave}`);
  }

  if (naoEncontrado) {
    return (
      <div className="grid min-h-60 place-items-center text-center">
        <div>
          <p className="tipo-corpo text-text-secondary">Registro não encontrado.</p>
          <Button variant="fantasma" className="mt-3" onClick={() => navegar(`/cadastro/${descritor.chave}`)}>
            <ArrowLeft /> Voltar para {descritor.titulo}
          </Button>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="grid min-h-60 place-items-center">
        <Marca tamanho={48} className="animate-pulse" />
      </div>
    );
  }

  // Escape hatch total (rastreador): abandona a edição genérica, mantém a rota.
  if (descritor.telaEdicaoCustom) {
    const Custom = descritor.telaEdicaoCustom;
    return <Custom item={item} recarregar={carregar} />;
  }

  const Icone = descritor.icone;
  const podeEditar = descritor.capacidades.podeEditar;
  const podeExcluir = descritor.capacidades.podeExcluir;
  const temPaineis = !!descritor.paineis?.length;
  const abas = descritor.abas;

  return (
    <Pagina
      titulo={descritor.rotuloItem(item)}
      sobretitulo={`${podeEditar ? 'Editar' : 'Detalhe'} · ${descritor.tituloSingular}`}
      icone={Icone}
      aoVoltar={() => navegar(`/cadastro/${descritor.chave}`)}
    >
      {abas?.length ? (
        <AbasEdicao
          abas={abas}
          ativa={abaAtiva}
          onChange={setAbaAtiva}
          descritor={descritor}
          item={item}
          valores={valores}
          onChangeCampo={ajustar}
          podeEditar={podeEditar}
          recarregar={carregar}
        />
      ) : (
        /* Sem abas: formulário e correlações lado a lado — a página cresce em largura,
           não em altura. `fieldset disabled` desativa os inputs sem PATCH. */
        <div
          className={cn(
            'grid grid-cols-1 gap-6',
            temPaineis && 'xl:grid-cols-[minmax(0,7fr)_minmax(0,4fr)]',
          )}
        >
          <fieldset disabled={!podeEditar} className="flex min-w-0 flex-col gap-6">
            {descritor.gruposEdicao.map((grupo) => (
              <GrupoCampos key={grupo.id} grupo={grupo} valores={valores} onChange={ajustar} />
            ))}
          </fieldset>

          {temPaineis && (
            <div className="flex min-w-0 flex-col gap-6">
              <PaineisEdicao descritor={descritor} item={item} recarregar={carregar} />
            </div>
          )}
        </div>
      )}

      <BarraAcoesEdicao
        podeEditar={podeEditar}
        podeExcluir={podeExcluir}
        temMudancas={temMudancas}
        salvando={salvando}
        excluindo={excluindo}
        rotulo={descritor.rotuloItem(item)}
        onSalvar={salvar}
        onExcluir={excluir}
      />
    </Pagina>
  );
}

/**
 * A edição em abas: a barra + a aba ativa (seus grupos de campos e seus painéis). O salvar segue
 * global (PATCH do que mudou em qualquer aba), então trocar de aba nunca perde alteração.
 */
function AbasEdicao({
  abas,
  ativa,
  onChange,
  descritor,
  item,
  valores,
  onChangeCampo,
  podeEditar,
  recarregar,
}: {
  abas: AbaEdicao<RecursoBase>[];
  ativa: string;
  onChange: (id: string) => void;
  descritor: DescritorRecurso;
  item: RecursoBase;
  valores: Valores;
  onChangeCampo: (chave: string, v: ValorCampo) => void;
  podeEditar: boolean;
  recarregar: () => Promise<void>;
}) {
  const aba = abas.find((a) => a.id === ativa) ?? abas[0];
  const grupos = descritor.gruposEdicao.filter((g) => aba.grupos?.includes(g.id));
  return (
    <div className="flex flex-col gap-6">
      <Abas
        abas={abas.map((a) => ({ id: a.id, rotulo: a.rotulo }))}
        ativa={aba.id}
        onChange={onChange}
      />
      {grupos.length > 0 && (
        <fieldset disabled={!podeEditar} className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2">
          {grupos.map((g) => (
            <GrupoCampos key={g.id} grupo={g} valores={valores} onChange={onChangeCampo} />
          ))}
        </fieldset>
      )}
      {aba.paineis?.length ? (
        <PaineisEdicao
          descritor={descritor}
          item={item}
          recarregar={recarregar}
          paineis={aba.paineis}
        />
      ) : null}
    </div>
  );
}
